import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { IdentificationObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type IdentificationStoreAction =
  | { type: "SET_IDENTIFICATION"; payload: IdentificationObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_IDENTIFICATION"; payload: IdentificationObj }
  | { type: "DELETE_IDENTIFICATION"; payload: string }
  | { type: "SET_IDENTIFICATION_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_IDENTIFICATION_SAVING_NEW"; payload: boolean };

// Reducer Function
const IdentificationStoreReducer = (
  state: IdentificationStoreState,
  action: IdentificationStoreAction
): IdentificationStoreState => {
  switch (action.type) {
    case "SET_IDENTIFICATION":
      return {
        ...state,
        identifications: action.payload,
        identificationsLoading: false,
      };

    case "SET_LOADING":
      return { ...state, identificationsLoading: action.payload };

    case "ADD_OR_UPDATE_IDENTIFICATION":
      const index = state.identifications.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedidentifications = [...state.identifications];
      if (index > -1) {
        updatedidentifications[index] = action.payload;
      } else {
        updatedidentifications.push(action.payload);
      }
      return { ...state, identifications: updatedidentifications };

    case "DELETE_IDENTIFICATION":
      return {
        ...state,
        identifications: state.identifications.filter(
          (l) => l.id !== action.payload
        ),
      };
    case "SET_IDENTIFICATION_FILES_TO_UPLOAD":
      return { ...state, identificationsFilestoUpload: action.payload };
    case "SET_IDENTIFICATION_SAVING_NEW":
      return { ...state, identificationSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface IdentificationStoreState {
  identifications: IdentificationObj[];
  identificationsLoading: boolean;
  identificationSavingNew: boolean;
  identificationsFilestoUpload: File[];
}

// Hook
export const useIdentificationStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(IdentificationStoreReducer, {
    identifications: [],
    identificationsLoading: true,
    identificationsFilestoUpload: [],
    identificationSavingNew: false,
  });

  const deleteIdentificationById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchIdentificationData = useV3GenericGetData<IdentificationObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createIdentification = useV3Create<IdentificationObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateIdentification = useV3Update<IdentificationObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<IdentificationObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleIdentificationFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.identificationsFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "identifications",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_IDENTIFICATION",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_IDENTIFICATION_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.identificationsFilestoUpload, ud_id, uploadDocument]
  );

  const handleDownloadIdentificationDocument = async (
    identification_id: string,
    document_id: string | null
  ) => {
    if (!document_id || !identification_id || !ud_id) {
      return Promise.reject("No id provided");
    }

    toast.success("Downloading file...");

    const params: any = {
      document_id: document_id,
      entity: "identifications",
      entity_id: identification_id,
      ud_id: ud_id,
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in Identification objects' documents array
          downloadFileFromS3(
            res,
            state.identifications
              ?.find((p) => p.id === identification_id)
              ?.documents?.find((d) => d.document_id === document_id)
              ?.document_name || "Snug_Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document with message "No response"`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
      });
  };

  const handleDeleteIdentificationDocument = useCallback(
    async (
      identification_id: string,
      document_id: string | null
    ): Promise<void> => {
      if (!document_id || !identification_id || !ud_id) {
        return Promise.reject("No id provided");
      }
      
      try {
        dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "identifications",
          entity_id: identification_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the Identification object
          const updatedIdentification = state.identifications.map((p) => {
            if (p.id === identification_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_IDENTIFICATION",
            payload: updatedIdentification,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.identifications, ud_id, deleteDocumentById]
  );

  const handleIdentificationFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_IDENTIFICATION_FILES_TO_UPLOAD",
        payload: [...state.identificationsFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.identificationsFilestoUpload]
  );

  const handleIdentificationFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.identificationsFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_IDENTIFICATION_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.identificationsFilestoUpload]
  );

  const handleIdentificationFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_IDENTIFICATION_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchIdentificationItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchIdentificationData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "identifications",
        });

        if (isMounted()) {
          dispatch({ type: "SET_IDENTIFICATION", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchIdentificationData, isMounted]
  );

  const handleAddOrUpdateIdentificationSingleCall = useCallback(
    async (
      IdentificationItem: IdentificationObj
    ): Promise<IdentificationObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(IdentificationItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateIdentification(
            {
              ud_id: ud_id,
              entity: "identifications",
              entity_id: IdentificationItem.id || undefined,
            },
            IdentificationItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createIdentification(
            { ud_id: ud_id, entity: "identifications" }, // Adjust according to your actual API parameter requirements
            IdentificationItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_IDENTIFICATION", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save Medical Allergy item.");
        console.error(error);
        return null;
      }
    },
    [ud_id, createIdentification, updateIdentification]
  );

  const handleAddOrUpdateIdentification = useCallback(
    async (IdentificationItem: IdentificationObj) => {
      dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: true });
      if (state.identificationsFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateIdentificationSingleCall(
            IdentificationItem
          );
          if (res) {
            await handleIdentificationFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical Allergy data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateIdentificationSingleCall(IdentificationItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical Allergy data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateIdentificationSingleCall,
      state.identificationsFilestoUpload,
      handleIdentificationFilesUpload,
    ]
  );

  const handleDeleteIdentification = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteIdentificationById({
          ud_id: ud_id,
          entity: "identifications",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_IDENTIFICATION", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_IDENTIFICATION_SAVING_NEW", payload: false });
      }
    },
    [deleteIdentificationById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchIdentificationItems(ud_id); // Fetch all Identification items
    }
  }, [ fetchIdentificationItems, ud_id]);

  return {
    identifications: state.identifications,
    identificationsLoading: state.identificationsLoading,
    identificationSavingNew: state.identificationSavingNew,
    identificationsFilestoUpload: state.identificationsFilestoUpload,
    handleAddOrUpdateIdentification,
    handleDeleteIdentification,
    handleIdentificationFileRemove,
    handleIdentificationFilesDrop,
    handleIdentificationFilesRemoveAll,
    handleDownloadIdentificationDocument,
    handleDeleteIdentificationDocument,
    handleIdentificationFilesUpload,
  };
};
